import { Injectable } from '@angular/core';

import * as d3 from 'd3';
import { DateTime } from 'luxon';

import { Observable } from 'rxjs';
import { map, concatAll, toArray, filter, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { BaseMiscDomainService } from '@common/domain/base-misc-domain.service';
import { SelectOption, SelectOptionNumber } from '@ui/components/form/common';
import * as fromPrtgValuesStoreActions from '@apps/prtg/store/values/values.actions';
import { MediaTypes, UtilsService } from '@common/services';
import { SummaryCounter } from '@common/models';
import { groupedCounter } from '@common/operators';

import { ServiceMiscApiService } from '../api';
import { IAuroraState } from '../../aurora.state';
import { ServiceFlatNode, ServicesFilter } from '../models';
import { ServiceStatus, PrtgConstants } from '../prtg-constants';
import { ServiceHistorical, Item, HistoricalData, HistoricalSerie } from '../models/historical';
import { FlatItem } from '../models/historical/flat-item';
import { HttpEvent } from '@angular/common/http';

@Injectable({
    providedIn: 'root'
})
export class ServiceMiscDomainService extends BaseMiscDomainService {
    constructor(
        private serviceMiscApiService: ServiceMiscApiService,
        private store: Store<IAuroraState>
    ) {
        super();
    }

    getServices(page: number = 1, limit: number = 100): Observable<ServiceFlatNode[]> {
        const flatNodes: ServiceFlatNode[] = [];

        const codeStatuses: Set<string> = new Set<string>();
        const statuses: SelectOption[] = [];

        const types: Set<string> = new Set<string>();

        return this.serviceMiscApiService.getServices(page, limit)
            .pipe(
                map((data: any) => {
                    data.items.forEach(item => {
                        if (!codeStatuses.has(item.status)) {
                            codeStatuses.add(item.status);
                            statuses.push(new SelectOptionNumber(parseInt(ServiceStatus[item.status], 10), item.status));
                        }

                        if (!types.has(item.categoryName)) {
                            types.add(item.categoryName);
                        }

                        flatNodes.push(ServiceFlatNode.mapperFromPrtg(item));
                    });

                    this.store.dispatch(
                        fromPrtgValuesStoreActions.PrtgServicesStatusesListLoaded({
                            servicesStatuses: statuses
                        })
                    );

                    this.store.dispatch(
                        fromPrtgValuesStoreActions.PrtgServicesTypesListLoaded({
                            servicesTypes: [...types].map((v: string) =>
                                new SelectOption(v, v)
                            )
                        })
                    );

                    // Dispatch pagination info to store
                    this.store.dispatch(
                        fromPrtgValuesStoreActions.PrtgServicesPaginationLoaded({
                            total: data.total,
                            pages: data.pages,
                            currentPage: data.currentPage,
                            limit: data.limit
                        })
                    );

                    return flatNodes;
                })
            );
    }

    getSummaryServiceByStatus(): Observable<SummaryCounter<string>[]> {
        return this.getServices()
            .pipe(
                take(1),
                groupedCounter<ServiceFlatNode, string>((flatNode: ServiceFlatNode) =>
                    flatNode.codeStatus
                )
            );
    }

    // HistoricalData[]
    getServiceHistory(serviceId: string, page: number = 1, limit: number = 100,since?:DateTime,until?:DateTime): Observable<any> {
        return this.serviceMiscApiService.getServiceHistory(serviceId, page, limit,since,until)
            .pipe(
                map(ServiceHistorical.mapperFromApi),
                concatAll(),
                map(Item.flatten),
                concatAll(),
                map((flatItem: FlatItem) => {
                    return flatItem;
                }),
                toArray(),
                map((flatItems: FlatItem[]) =>
                    Array.from(UtilsService.groupByTyped<number, FlatItem>(flatItems, fi => fi.channelId))
                        .map((record: [number, FlatItem[]]) =>
                            ({ channel: record[0], data: record[1] })
                        )
                ),
                concatAll(),
                filter((record: { channel: number, data: FlatItem[]; }) =>
                    record.data.some((fi: FlatItem) =>
                        fi.rawValue !== null
                    )
                ),
                map((record: { channel: number, data: FlatItem[]; }) => {
                    const unit = record.data.find((fi: FlatItem) => !UtilsService.isNullOrEmpty(fi.unit)).unit;

                    record.data
                        .filter((fi: FlatItem) =>
                            UtilsService.isNullOrEmpty(fi.unit)
                        )
                        .forEach((fi: FlatItem) =>
                            fi.unit = unit
                        );

                    return record.data;
                }),
                map((flatItems: FlatItem[]) =>
                    Array.from(UtilsService.groupByTyped<string, FlatItem>(flatItems, fi => `${fi.unit}|${fi.channelId}`))
                        .map((value: [string, FlatItem[]]) =>
                            ({ unit: value[0].split('|')[0], series: value[1] })
                        )
                ),
                concatAll(),
                map((record: { unit: string, series: FlatItem[] }) => {
                    const historicalData: HistoricalData = new HistoricalData(record.unit);

                    historicalData.historicalSeries = Array.from(UtilsService.groupBy(record.series, s => s.channelId))
                        .map((value: [number, FlatItem[]]) => {
                            const channelId = value[0];
                            const channel = value[1][0].channel;
                            const historicalSerie: HistoricalSerie = new HistoricalSerie(channelId, channel, historicalData.unit);

                            historicalSerie.data = value[1];

                            historicalData.unit = historicalSerie.unit;

                            return historicalSerie;
                        });

                    return historicalData;
                }),
                toArray(),
                map((hd: HistoricalData[]) =>
                    this.sortHistoricalData(hd)
                )
            );
    }

    export(servicesFilter: ServicesFilter, format: MediaTypes): Observable<HttpEvent<Blob>> {
        return this.serviceMiscApiService.export(servicesFilter, format);
    }

    sortHistoricalData(hd: HistoricalData[]): any {
        return hd.sort((hda, hdb) => hdb.channelId - hda.channelId);
    }
}
